'use es6';

import { fromJS, List } from 'immutable';
import { SEARCH } from '../../../constants/configTypes';
import { COUNT } from '../../../constants/metricTypes';
import { DESC } from '../../../constants/sortOrder';
import { Config } from '../..';
import { Metric } from '../../metrics';
import { makeFilters } from '../filters';
import { CRM_OBJECT, DEAL_CREATE_ATTRIBUTION, DEALS } from '../../../constants/dataTypes';
import { defaultProperties } from '../defaultProperties';
import { buildDrilldownReport } from '../report';
import { has } from '../../../lib/has';
import { propertyLabelTranslator } from 'property-translator/propertyTranslator';
import campaign from '../../../references/campaign';
import company from '../../../references/company';
import contact from '../../../references/contact';
import deal from '../../../references/deal';
import owner from '../../../references/owner';
import pipeline from '../../../references/pipeline';
import { ALLOWLISTED_HUBSPOT_DEFINED_CRM_OBJECTS } from '../../../crmObjects/utils';
const DEAL_CREATE_ATTRIBUTION_OBJECT_TYPE_ID = ALLOWLISTED_HUBSPOT_DEFINED_CRM_OBJECTS.get(DEAL_CREATE_ATTRIBUTION);
const idLookup = referenceLookup => id => referenceLookup(List([id])).then(v => v.valueSeq().getIn([0, 'label']));
const propertyReferences = {
  hs_campaign_guid: idLookup(campaign),
  hs_contact_company: idLookup(company),
  hs_contact_id: idLookup(contact),
  hs_deal_id: idLookup(deal),
  hs_deal_pipeline: pipelineId => pipeline(DEALS)().then(options => {
    const matches = options.filter(({
      value
    }) => value === pipelineId);
    if (matches && matches.length) {
      return matches[0].label;
    }
    return null;
  }),
  hs_deal_owner: idLookup(owner)
};
const buildDrilldownName = (baseReport, keys) => {
  const rawLabels = keys.skipLast(2) // The last two keys are the metric and its type
  .zip(baseReport.getIn(['config', 'dimensions'])).map(([value, property]) => ({
    value,
    property
  }));
  const hydratedLabelPromises = rawLabels.map(({
    property,
    value
  }) => {
    const propertyLabel = propertyLabelTranslator(property.toLowerCase(), 'dealCreateAttributionProperties');
    let valuePromise;
    if (has(propertyReferences, property)) {
      valuePromise = propertyReferences[property](value);
    } else {
      valuePromise = Promise.resolve(propertyLabelTranslator(value.toLowerCase(), 'dealCreateAttributionProperties'));
    }
    return valuePromise.then(valueLabel => `${propertyLabel}: ${valueLabel}`);
  });
  return Promise.all(hydratedLabelPromises).then(labels => labels.join(' & '));
};
const getDrilldownMetrics = baseReport => {
  const metricProperties = baseReport.getIn(['config', 'metrics']).map(metric => metric.get('property'));
  return [...defaultProperties[DEAL_CREATE_ATTRIBUTION], ...metricProperties].map(property => Metric({
    property,
    metricTypes: [COUNT]
  }));
};
const buildDrilldown = (baseReport, keys, options) => {
  return makeFilters(baseReport.get('config'), keys, options).then(filters => {
    return Config({
      configType: SEARCH,
      dataType: CRM_OBJECT,
      objectTypeId: DEAL_CREATE_ATTRIBUTION_OBJECT_TYPE_ID,
      dimensions: List.of(),
      filters,
      limit: baseReport.getIn(['config', 'limit'], 5),
      metrics: getDrilldownMetrics(baseReport),
      sort: fromJS([{
        property: 'hs_interaction_timestamp',
        order: DESC
      }])
    });
  }).then(generatedConfig => buildDrilldownReport(baseReport, generatedConfig, options));
};
const getAttributionDrilldown = (baseReport, keys, options) => {
  return Promise.all([buildDrilldown(baseReport, keys, options), buildDrilldownName(baseReport, keys)]).then(([drilldownReport, drilldownName]) => {
    return drilldownReport.set('name', drilldownName).setIn(['displayParams', 'columns'], fromJS({
      hs_asset_url: {
        hideColumn: true
      }
    }));
  });
};
export default {
  isSupported: report => report.getIn(['config', 'dataType']) === CRM_OBJECT && report.getIn(['config', 'objectTypeId']) === DEAL_CREATE_ATTRIBUTION_OBJECT_TYPE_ID,
  generate: getAttributionDrilldown
};